/** MK-07: the industry pages (`/for/<industry>`), `/themes` and `/hardware` of the marketing site (`web` namespace). */
export const industries = {
    common: {
        eyebrow: "For {{name}}",
        challenges: "What gets in the way",
        youGet: "What you get",
        themesTitle: "Website designs for {{name}}",
        themesBody: "Start from a design made for shops like yours. Every one is a real, working demo shop.",
        featuresTitle: "Where to look next",
    },
    fashion: {
        name: "fashion shops",
        nav: "Fashion",
        metaDescription: "For clothing and fashion shops in Bangladesh: one stock for every size and colour across the shop, Facebook and your website, couriers and COD in one place.",
        title: "Every size and colour, right in the shop and online",
        body: "Sell the same kurti at the counter, on Facebook and on your website without the last medium being sold twice.",
        challenges: {
            a: {
                title: "Sizes and colours run out unevenly",
                body: "Stock per size and colour, live in every branch, with low-stock alerts before the popular ones run out.",
            },
            b: {
                title: "Facebook orders and returns pile up",
                body: "One order list for every channel, one-click courier booking, and returns and exchanges that put stock back.",
            },
            c: {
                title: "Eid rush at the counter",
                body: "A POS that bills fast and keeps going offline, with exchanges straight from the receipt.",
            },
        },
        points: {
            p1: "Variants for size, colour and fabric with their own barcodes",
            p2: "Size charts on the product page",
            p3: "Fake-order check before you ship",
            p4: "Profit per product and per channel",
        },
    },
    grocery: {
        name: "grocery shops",
        nav: "Grocery",
        metaDescription: "For grocery and super shops: fast barcode billing that works offline, loose items by weight, stock counts without closing, and home delivery from your own website.",
        title: "A busy counter that never waits for the internet",
        body: "Bill hundreds of items an hour by barcode or weight, and take home-delivery orders from the same stock.",
        challenges: {
            a: {
                title: "Long queues in the evening",
                body: "Scan, quick keys for loose items and quantity multipliers keep the line moving; the POS never waits on the network.",
            },
            b: {
                title: "Thousands of items, small margins",
                body: "Purchase orders, supplier dues and profit per product show where the margin goes.",
            },
            c: {
                title: "Counting stock means closing",
                body: "Count shelves while you sell; sales during the count are taken into account.",
            },
        },
        points: {
            p1: "Items sold by weight or piece",
            p2: "Barcode labels for your own packs",
            p3: "Delivery zones and charges for home delivery",
            p4: "Shift cash counts for every cashier",
        },
    },
    cosmetics: {
        name: "beauty and cosmetics shops",
        nav: "Cosmetics",
        metaDescription: "For beauty and cosmetics sellers: Facebook ad landing pages, Pixel and Conversions API, fake-order check, COD matching and one stock for shop and online.",
        title: "Sell from your Facebook ads without losing track",
        body: "Run ads to a landing page, take the order, check it is real and send it by courier — with the stock right in the shop too.",
        challenges: {
            a: {
                title: "Ads bring orders, and fake orders",
                body: "A risk score from each phone number's history and a blocklist for numbers that refuse parcels.",
            },
            b: {
                title: "Not knowing which ad sells",
                body: "Facebook Pixel and the Conversions API on your shop, with orders counted once.",
            },
            c: {
                title: "COD money arrives in pieces",
                body: "Courier payouts matched to orders, so you see what is still owed.",
            },
        },
        points: {
            p1: "Landing pages with the order form on the page",
            p2: "Offers and coupons",
            p3: "One-click Steadfast, Pathao or RedX",
            p4: "One stock for the shop and online",
        },
    },
    gadgets: {
        name: "gadget and electronics shops",
        nav: "Gadgets",
        metaDescription: "For mobile, gadget and electronics shops: stock per branch, purchase orders and supplier dues, detailed product specs online and exact profit per item.",
        title: "Expensive stock, counted to the last piece",
        body: "Know where every phone and charger is, what it cost and what you earned on it — in every branch and online.",
        challenges: {
            a: {
                title: "High-value stock across branches",
                body: "Live stock per branch, transfers with send and receive steps and a history of every change.",
            },
            b: {
                title: "Thin margins on fast-moving items",
                body: "Profit per item after cost, and supplier dues you can see at a glance.",
            },
            c: {
                title: "Customers compare specs online",
                body: "Product pages with full specifications and related products in the gadgets design.",
            },
        },
        points: {
            p1: "Purchase orders and goods receipts",
            p2: "Barcode labels on arrival",
            p3: "Returns and exchanges from the receipt",
            p4: "VAT invoices",
        },
    },
    jewellery: {
        name: "jewellery shops",
        nav: "Jewellery",
        metaDescription: "For jewellery shops: careful stock of high-value pieces, manager approval for discounts, a cash count at every shift, and an elegant website design.",
        title: "Every piece accounted for",
        body: "High-value stock, careful discounts and a cash count you can trust at the end of every shift.",
        challenges: {
            a: {
                title: "A single missing piece is costly",
                body: "Every item's history — received, moved, sold or adjusted — with who did it.",
            },
            b: {
                title: "Discounts at the counter",
                body: "Discounts above your limit need a manager's PIN, and every one shows in the reports.",
            },
            c: {
                title: "Cash at closing",
                body: "Shifts open with a float and close with a blind count, showing the difference at once.",
            },
        },
        points: {
            p1: "An elegant jewellery website design",
            p2: "Audit trail for sensitive changes",
            p3: "Profit per piece",
            p4: "Bengali receipts and invoices",
        },
    },
    bookshop: {
        name: "bookshops",
        nav: "Bookshops",
        metaDescription: "For bookshops and publishers: printed books and eBooks side by side, author and publisher pages, Bangla-first design, counter POS and courier delivery.",
        title: "Printed books and eBooks, one shop",
        body: "Sell at the counter, deliver printed books by courier and let readers start an eBook the minute they pay.",
        challenges: {
            a: {
                title: "Thousands of titles",
                body: "Authors, publishers and categories, searchable in Bengali and English.",
            },
            b: {
                title: "Readers want eBooks too",
                body: "Add an eBook edition to any book, with a free sample and a reading library for each customer.",
            },
            c: {
                title: "Book fairs and the counter",
                body: "A POS that works offline at a stall and syncs when it is back online.",
            },
        },
        points: {
            p1: "The Boighor bookshop design",
            p2: "bKash payment for eBooks",
            p3: "Watermarked pages",
            p4: "Courier delivery for printed books",
        },
    },
    "home-kitchen": {
        name: "home and kitchen shops",
        nav: "Home & kitchen",
        metaDescription: "For home, kitchen and furniture sellers: delivery charges by area and weight, courier booking, single-product landing pages and one stock across branches.",
        title: "Bulky items, delivered right",
        body: "Charge delivery by area, book the courier from the order and keep the shop and website on one stock.",
        challenges: {
            a: {
                title: "Delivery costs vary by area",
                body: "Delivery zones with their own charges and delivery days, shown before the customer orders.",
            },
            b: {
                title: "One hero product sells the most",
                body: "A landing page for it, with the order form and offer on the page.",
            },
            c: {
                title: "Stock in a warehouse and a shop",
                body: "Live stock per location and transfers between them.",
            },
        },
        points: {
            p1: "The home and kitchen design",
            p2: "Courier booking and COD matching",
            p3: "Purchase orders and supplier dues",
            p4: "Profit per product",
        },
    },
    kids: {
        name: "kids and baby shops",
        nav: "Kids & baby",
        metaDescription: "For kids and baby shops: variants by age and size, gift-friendly offers, Facebook orders and a cheerful website design on one stock.",
        title: "Sizes by age, stock that stays right",
        body: "Clothes, toys and baby care with variants by age and size — sold in the shop, on Facebook and on your website.",
        challenges: {
            a: {
                title: "Children grow, sizes change",
                body: "Variants by age and size with their own stock, and low-stock alerts on the ones that sell.",
            },
            b: {
                title: "Parents order from their phones",
                body: "A cheerful, fast website design, and Facebook orders in the same list.",
            },
            c: {
                title: "Gifts and returns",
                body: "Exchanges straight from the receipt, with stock put back.",
            },
        },
        points: {
            p1: "The kids and baby design",
            p2: "Offers and coupons",
            p3: "One-click courier booking",
            p4: "One stock for the shop and online",
        },
    },
};
export const themesPage = {
    metaTitle: "Website themes",
    metaDescription: "12 online shop designs made for Bangladeshi businesses — fashion, grocery, gadgets, books, beauty and more. Open each one as a live demo shop.",
    eyebrow: "Website themes",
    title: "12 designs made for Bangladeshi shops",
    body: "Each design is a real, working demo shop. Open one, add something to the cart, and see how your customers will shop.",
    visit: "Open the live demo",
    preview: "Preview",
    shotAlt: "The {{name}} theme on a computer",
    phoneAlt: "The {{name}} theme on a phone",
    ctaTitle: "Your shop, in your design",
    ctaBody: "Start free, pick a design and publish today. You can change it later without losing anything.",
    descriptions: {
        fashion_women: "Big photos, size choices and offers up front, for women's clothing and sarees.",
        fashion_men: "A clean, bold layout for panjabis, shirts and menswear.",
        cosmetics: "Soft and bright, made for beauty products and skin care.",
        gadgets: "Specs, comparisons and related items, for phones and electronics.",
        grocery: "Quick to browse by category, with weight and pack sizes.",
        home_kitchen: "Room by room, for home, kitchen and furniture.",
        kids_baby: "Cheerful and easy, for clothes, toys and baby care.",
        jewellery: "Elegant and calm, letting each piece stand out.",
        home_food: "Warm and homely, for home cooks and food businesses.",
        books: "Boighor: Bangla-first, with authors, publishers and eBooks.",
        single_product: "One product, one page, with the order form built in — for ads.",
        minimal: "Simple and quiet, fits any kind of shop.",
    },
};
export const hardware = {
    metaTitle: "Hardware",
    metaDescription: "What you need to run the {{brand}} POS: a Windows or Mac computer, a 58 or 80 mm thermal printer, a USB barcode scanner, a cash drawer and a label printer.",
    eyebrow: "Hardware",
    title: "Works with the hardware shops already use",
    body: "No special machines. A normal computer, a thermal printer and a barcode scanner are enough to start; add a cash drawer and a label printer when you need them.",
    soldSeparately: "Hardware is sold separately. Ask us before you buy and we will tell you which models we have tested.",
    ask: "Ask us on WhatsApp",
    askForm: "Send us a message",
    items: {
        computer: {
            title: "Computer",
            body: "The counter computer runs the POS.",
            points: {
                p1: "Windows 10 or later, or a Mac",
                p2: "A screen of at least 1366 × 768",
                p3: "A touch screen works too, but is not needed",
            },
        },
        printer: {
            title: "Receipt printer",
            body: "Any ordinary thermal printer prints Bengali receipts — the receipt is sent as a picture, so the printer needs no Bengali font.",
            points: {
                p1: "58 mm or 80 mm thermal paper",
                p2: "USB or network (ESC/POS)",
                p3: "Opens the cash drawer too",
            },
        },
        scanner: {
            title: "Barcode scanner",
            body: "Any scanner that types like a keyboard works without setup.",
            points: {
                p1: "USB or Bluetooth",
                p2: "Reads EAN-13, UPC-A and Code 128",
                p3: "Also used for stock counts",
            },
        },
        drawer: {
            title: "Cash drawer",
            body: "Plugs into the receipt printer and opens when a cash sale is completed.",
            points: {
                p1: "Connects to the printer (RJ11)",
                p2: "Opening it without a sale needs a manager",
                p3: "Counted at every shift close",
            },
        },
        labels: {
            title: "Barcode labels",
            body: "Print price and barcode stickers for your products — on A4 sticker sheets or a label printer's roll.",
            points: {
                p1: "Name, price, MRP and barcode",
                p2: "38 × 25 mm labels scan reliably",
                p3: "Print right after receiving goods",
            },
        },
    },
};
//# sourceMappingURL=web-solutions.js.map